import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, MessageCircle, Heart, Users, Flame, Moon, Brain, Lightbulb, Pill, Trophy, Sparkles, Flag } from "lucide-react";
import { ReportContentDialog } from "@/components/community/ReportContentDialog";
import { ReactionButton } from "@/components/community/ReactionButton";
import { useToast } from "@/hooks/use-toast";
import { CreatePostDialog } from "@/components/community/CreatePostDialog";
import { formatDistanceToNow } from "date-fns";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { sanitizeText } from "@/lib/sanitize";
import womenCommunity from "@/assets/women-community-support.jpg";

type Category = 'general' | 'hot_flashes' | 'sleep_issues' | 'emotional_wellbeing' | 'treatments' | 'lifestyle_tips' | 'success_stories';

interface Post {
  id: string;
  title: string;
  content: string;
  category: Category;
  is_anonymous: boolean;
  created_at: string;
  user_id: string | null;
  author_display_name?: string | null;
  author_avatar_url?: string | null;
  community_comments_public?: Array<{ count: number }>;
  comment_count?: number;
  reaction_count?: number;
  user_roles?: string[];
  preview_comments?: Array<{
    id: string;
    content: string;
    created_at: string;
    is_anonymous: boolean;
    user_id: string;
    author_display_name?: string | null;
  author_avatar_url?: string | null;
    user_roles?: string[];
  }>;
}

const categoryConfig = {
  general: { icon: MessageCircle, label: "General Discussion", color: "bg-primary/10 text-primary" },
  hot_flashes: { icon: Flame, label: "Hot Flashes", color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" },
  sleep_issues: { icon: Moon, label: "Sleep Issues", color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" },
  emotional_wellbeing: { icon: Heart, label: "Emotional Wellbeing", color: "bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300" },
  treatments: { icon: Pill, label: "Treatments & HRT", color: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300" },
  lifestyle_tips: { icon: Lightbulb, label: "Lifestyle Tips", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300" },
  success_stories: { icon: Trophy, label: "Success Stories", color: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300" }
};

export default function Community() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<Category | 'all'>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportPostId, setReportPostId] = useState<string | undefined>();

  useEffect(() => {
    fetchPosts();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('community-posts-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'community_posts'
      }, () => {
        fetchPosts();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      let query = supabase
        .from('community_posts')
        .select('id, title, content, category, is_anonymous, created_at, visible_author_id, is_locked')
        .eq('moderation_status', 'approved')
        .order('created_at', { ascending: false });

      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      const { data: postsData, error } = await query;

      if (error) throw error;

      const postIds = (postsData || []).map(p => p.id);

      // Single query to get all comments for all posts
      const { data: allComments } = await supabase
        .from('community_comments')
        .select('id, post_id, content, created_at, is_anonymous, visible_author_id')
        .in('post_id', postIds)
        .eq('moderation_status', 'approved')
        .order('created_at', { ascending: false });

      // Only non-anonymous authors have a visible id
      const authorIds = Array.from(new Set([
        ...(postsData || []).map(p => p.visible_author_id),
        ...(allComments || []).map(c => c.visible_author_id),
      ].filter(Boolean))) as string[];

      const { data: authorProfiles } = authorIds.length
        ? await supabase
            .from('public_profiles')
            .select('user_id, display_name, avatar_url')
            .in('user_id', authorIds)
        : { data: [] as any[] };

      const { data: authorRoles } = authorIds.length
        ? await supabase
            .from('user_roles')
            .select('user_id, role')
            .in('user_id', authorIds)
        : { data: [] as any[] };

      const profileMap = (authorProfiles || []).reduce((acc, p: any) => {
        acc[p.user_id] = p;
        return acc;
      }, {} as Record<string, any>);

      const rolesMap = (authorRoles || []).reduce((acc, role: any) => {
        if (!acc[role.user_id]) acc[role.user_id] = [];
        acc[role.user_id].push(role.role);
        return acc;
      }, {} as Record<string, string[]>);

      // Build comment counts and previews from single query result
      const commentsByPost = (allComments || []).reduce((acc, comment: any) => {
        if (!acc[comment.post_id]) {
          acc[comment.post_id] = [];
        }
        acc[comment.post_id].push({
          ...comment,
          user_id: comment.visible_author_id,
          author_display_name: comment.visible_author_id ? profileMap[comment.visible_author_id]?.display_name : null,
          author_avatar_url: comment.visible_author_id ? profileMap[comment.visible_author_id]?.avatar_url : null,
          user_roles: comment.visible_author_id ? rolesMap[comment.visible_author_id] || [] : []
        });
        return acc;
      }, {} as Record<string, any[]>);

      const postsWithCounts = (postsData || []).map((post: any) => ({
        ...post,
        user_id: post.visible_author_id,
        author_display_name: post.visible_author_id ? profileMap[post.visible_author_id]?.display_name : null,
        author_avatar_url: post.visible_author_id ? profileMap[post.visible_author_id]?.avatar_url : null,
        user_roles: post.visible_author_id ? rolesMap[post.visible_author_id] || [] : [],
        comment_count: commentsByPost[post.id]?.length || 0,
        preview_comments: (commentsByPost[post.id] || []).slice(0, 2)
      }));

      setPosts(postsWithCounts as Post[]);

    } catch (error: any) {
      toast({
        title: "Error loading posts",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${womenCommunity})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <Link to="/">
            <Button variant="ghost" className="mb-4 gap-2 hover:scale-105 transition-transform">
              <ArrowLeft className="h-4 w-4" />
              Back Home
            </Button>
          </Link>
          
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-3 flex items-center gap-3 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                <Users className="h-10 w-10 text-primary" />
                Community Support
              </h1>
              <p className="text-lg text-muted-foreground flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Share experiences, ask questions, and support each other
              </p>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} size="lg" className="gap-2 hover:scale-105 transition-transform shadow-lg hover:shadow-xl">
              <Plus className="h-5 w-5" />
              New Post
            </Button>
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={(val) => setSelectedCategory(val as Category | 'all')} className="mb-8">
          <TabsList className="w-full justify-start flex-wrap h-auto gap-3 p-3 bg-background/80 backdrop-blur-xl border-2 border-primary/20 rounded-xl shadow-xl">
            <TabsTrigger 
              value="all" 
              className="gap-2 transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg hover:scale-105 rounded-lg py-2.5 px-4"
            >
              <Sparkles className="h-4 w-4" />
              All Posts
            </TabsTrigger>
            {Object.entries(categoryConfig).map(([key, config]) => {
              const Icon = config.icon;
              const gradients = {
                hot_flashes: 'data-[state=active]:from-red-500 data-[state=active]:to-orange-500',
                sleep_issues: 'data-[state=active]:from-indigo-500 data-[state=active]:to-purple-500',
                emotional_wellbeing: 'data-[state=active]:from-pink-500 data-[state=active]:to-rose-500',
                treatments: 'data-[state=active]:from-purple-500 data-[state=active]:to-violet-500',
                lifestyle_tips: 'data-[state=active]:from-yellow-500 data-[state=active]:to-amber-500',
                success_stories: 'data-[state=active]:from-green-500 data-[state=active]:to-emerald-500',
                general: 'data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500',
              };
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  className={`gap-2 transition-all duration-300 data-[state=active]:bg-gradient-to-r ${gradients[key as keyof typeof gradients]} data-[state=active]:text-white data-[state=active]:shadow-lg hover:scale-105 rounded-lg py-2.5 px-4`}
                >
                  <Icon className="h-4 w-4" />
                  {config.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* Posts List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading posts...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-semibold mb-2">No posts yet</h3>
            <p className="text-muted-foreground mb-6">
              Be the first to start a conversation in this category!
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
              <Plus className="h-5 w-5" />
              Create First Post
            </Button>
          </Card>
        ) : (
          <div className="space-y-5">
            {filteredPosts.map((post, index) => {
              const categoryInfo = categoryConfig[post.category];
              const CategoryIcon = categoryInfo.icon;
              
              return (
                <Card 
                  key={post.id} 
                  className="hover:shadow-2xl transition-all hover:-translate-y-1 border-2 hover:border-primary/50 bg-background/80 backdrop-blur-sm cursor-pointer animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => navigate(`/community/post/${post.id}`)}
                >
                  <CardHeader>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={post.author_avatar_url || undefined} />
                            <AvatarFallback className="text-xs bg-primary/10">
                              {post.is_anonymous ? "?" : (post.author_display_name?.charAt(0).toUpperCase() || "?")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground font-medium">
                              {post.is_anonymous ? "Anonymous" : (post.author_display_name || "Community Member")}
                            </span>
                          {!post.is_anonymous && post.user_roles && post.user_roles.length > 0 && (
                            <>
                              {post.user_roles.includes('admin') && (
                                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Admin</Badge>
                              )}
                              {post.user_roles.includes('moderator') && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Moderator</Badge>
                              )}
                            </>
                          )}
                            <span className="text-sm text-muted-foreground">•</span>
                            <span className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                        <Badge className={`${categoryInfo.color} shadow-md hover:scale-110 transition-transform`}>
                          <CategoryIcon className="h-3 w-3 mr-1" />
                          {categoryInfo.label}
                        </Badge>
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2 hover:text-primary transition-colors">{post.title}</CardTitle>
                        <CardDescription 
                          className="line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: sanitizeText(post.content) }}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-4 w-4" />
                        <span>{post.comment_count || 0}</span>
                      </div>
                      <ReactionButton postId={post.id} initialCount={post.reaction_count} />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReportPostId(post.id);
                          setReportDialogOpen(true);
                        }}
                        className="gap-1 ml-auto"
                      >
                        <Flag className="h-3 w-3" />
                        Report
                      </Button>
                    </div>

                    {/* Comment Previews */}
                    {post.preview_comments && post.preview_comments.length > 0 && (
                      <div className="space-y-3 pt-3 border-t">
                        {post.preview_comments.map((comment) => (
                          <div key={comment.id} className="flex gap-2 text-sm">
                            <Avatar className="h-6 w-6 mt-1">
                              <AvatarImage src={comment.author_avatar_url || undefined} />
                              <AvatarFallback className="text-xs bg-secondary/50">
                                {comment.is_anonymous ? "?" : (comment.author_display_name?.charAt(0).toUpperCase() || "?")}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-foreground">
                                  {comment.is_anonymous ? "Anonymous" : (comment.author_display_name || "Community Member")}
                                </span>
                                {!comment.is_anonymous && comment.user_roles && comment.user_roles.length > 0 && (
                                  <>
                                    {comment.user_roles.includes('admin') && (
                                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Admin</Badge>
                                    )}
                                    {comment.user_roles.includes('moderator') && (
                                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Moderator</Badge>
                                    )}
                                  </>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                </span>
                              </div>
                              <p 
                                className="text-muted-foreground line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: sanitizeText(comment.content) }}
                              />
                            </div>
                          </div>
                        ))}
                        {post.comment_count > 2 && (
                          <button 
                            className="text-sm text-primary hover:underline font-medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/community/post/${post.id}`);
                            }}
                          >
                            View all {post.comment_count} comments →
                          </button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <CreatePostDialog 
          open={createDialogOpen} 
          onOpenChange={setCreateDialogOpen}
          onPostCreated={fetchPosts}
        />

        <ReportContentDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          postId={reportPostId}
          contentType="post"
        />
      </div>
      </div>
    </div>
  );
}